import { Component, type ErrorInfo, type ReactNode } from "react";
import { isApiError } from "@brevalt/api-client";
import { ServerErrorState } from "./system-states";

interface Props {
  children: ReactNode;
  /** Replaces the default 500 state. */
  fallback?: (props: { error: unknown; reset: () => void }) => ReactNode;
  /** Error reporting hook (our own backend only; never a third party, AGENTS §4). */
  onError?: (error: unknown, info: ErrorInfo) => void;
}

interface State {
  error: unknown;
  failed: boolean;
}

/**
 * Catches render errors below it and shows the 500 state (screen 37) with the API request id when the cause is
 * an API error. "Try again" re-renders the children.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, failed: false };

  static getDerivedStateFromError(error: unknown): State {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null, failed: false });
  };

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const { error } = this.state;
    if (this.props.fallback) return this.props.fallback({ error, reset: this.reset });
    return <ServerErrorState requestId={isApiError(error) ? error.requestId : undefined} onRetry={this.reset} />;
  }
}
